<template>
    <div ref="container" class="xx-treeselect">
        <el-popover
            ref="popover"
            popper-class="xx-tree-wrapper"
            placement="bottom-start"
            :width="treeWidth"
            trigger="manual"
            v-bind="$attrs"
            v-model="popoverVisible"
        >
            <slot name="prefix"></slot>
            <tree-wrapper
                ref="treeWrapper"
                v-bind="$attrs"
                :data="data"
                :value="innerValue"
                :keyword="keyword"
                :disabled="selectDisabled"
                @change="change"
            ></tree-wrapper>
            <slot name="append"></slot>
            <div class="no-outline" slot="reference">
                <view-wrapper
                    ref="viewWrapper"
                    v-bind="$attrs"
                    :tag-data="tagData"
                    :size="_size"
                    :disabled="selectDisabled"
                    @filter="filterData"
                ></view-wrapper>
            </div>
        </el-popover>
    </div>
</template>
<script>
import emitter from 'element-ui/lib/mixins/emitter';
import { deepClone } from '@lsfe/tools';
import TreeWrapper from './TreeWrapper';
import ViewWrapper from './ViewWrapper';
import { getValue, getItems } from './utils';

export default {
    name: 'XxTreeselect',
    components: {
        TreeWrapper,
        ViewWrapper
    },
    mixins: [emitter],
    props: {
        multiple: [Boolean],
        nodeConfig: {
            type: Object
        },
        value: {
            required: true
        },
        data: {
            type: [Array]
        },
        size: {
            type: String
        },
        placeholder: {
            type: String,
            default: ''
        },
        clearable: {
            type: Boolean,
            default: true
        },
        disabled: {
            type: Boolean,
            default: false
        },
        // 是否使用checkBox进行多选
        showCheckbox: {
            type: Boolean,
            default: true
        },
        onlyLeaf: {
            type: Boolean,
            default: true
        },
        // 数据是否向上收敛，即所有子选项选中时，只返回父节点的数据
        mergeValue: {
            type: Boolean,
            default: false
        },
        // 展示tag向上收敛，即所有子选项选中时，展示父节点的tag
        mergeTag: {
            type: Boolean,
            default: true
        },
        // 渲染选中项展示tag的方法，仅在多选时有效
        renderTag: {
            type: Function
        },
        // 是否选择后关闭下拉框，只在单选时有效
        closeAfterSelect: {
            type: Boolean,
            default: true
        },
        // 是否需要生成树形数据
        needGenTree: {
            type: Boolean,
            default: true
        },
        // 判断是否可选的方法
        editableMethod: [Function],
        // 渲染节点的下拉项节点的方法
        renderNode: [Function],
        // 控制树形下拉框宽度的属性
        treeWidth: {
            type: Number,
            default: 300
        },
        filterMethod: {
            type: Function
        },
        filterNodeMethod: {
            type: Function
        }
    },
    provide() {
        return {
            treeSelect: this
        };
    },
    inject: {
        elForm: {
            default: ''
        },
        elFormItem: {
            default: ''
        }
    },
    computed: {
        selectDisabled() {
            return this.disabled || (this.elForm || {}).disabled;
        },
        checkbox() {
            return this.multiple && this.showCheckbox;
        },
        _size() {
            return this.size || (this.elFormItem || {}).elFormItemSize;
        }
    },
    watch: {
        value(value) {
            this.innerValue = value;
        },
        popoverVisible(visible) {
            if (visible) {
                this.$emit('focus');
            }else {
                this.$emit('blur');
            }
        }
    },
    data() {
        return {
            innerNodeConfig: {
                children: 'children',
                label: 'label',
                value: 'id'
            },
            popoverVisible: false,
            innerValue: undefined,
            tagData: undefined,
            keyword: '',
            startClickElement: {},
            shouldTrigger: false
        };
    },
    created() {
        this.initNodeConfig();
        this.innerValue = this.value;
    },
    mounted() {
        // 事件监听，点击关闭下拉框
        window.addEventListener('mousedown', this.handleBeforeClick);
        window.addEventListener('mouseup', this.triggerClick);
    },
    beforeDestroy() {
        window.removeEventListener('mousedown', this.handleBeforeClick);
        window.removeEventListener('mouseup', this.triggerClick);
    },
    methods: {
        handleBeforeClick(e) {
            this.startClickElement = e;
        },
        triggerClick(e) {
            const containerEl = this.$refs.container;
            const treeWrapperEl = this.$refs.treeWrapper.$el;
            if (
                !containerEl.contains(this.startClickElement.target) &&
                !treeWrapperEl.contains(this.startClickElement.target) &&
                !containerEl.contains(e.target) &&
                !treeWrapperEl.contains(e.target)
            ) {
                // shouldTrigger防止在点击其他地方时重复触发下面方法
                if (this.shouldTrigger) {
                    this.popoverVisible = false;
                    this.$refs.viewWrapper.clearKeyword();
                    this.dispatch('ElFormItem', 'el.form.blur', this.innerValue);
                }
                this.shouldTrigger = false;
            } else {
                this.shouldTrigger = true;
            }
        },
        // 初始化节点props参数
        initNodeConfig() {
            Object.assign(this.innerNodeConfig, this.nodeConfig);
        },
        change(data, needEmit, isFromeValueChange) {
            const value = getValue(data, this.onlyLeaf, this.mergeValue, this.multiple, this.innerNodeConfig);
            let items = getItems(data, this.onlyLeaf, this.mergeValue, this.multiple);
            if (this.multiple) {
                items = items.map(item => {
                    if (!item) return;
                    item = deepClone(item);
                    delete item.children;
                    delete item.disabled;
                    delete item.selfDisabled;
                    return item;
                });
            } else if (items) {
                items = deepClone(items);
                delete items.children;
                delete items.disabled;
                delete items.selfDisabled;
            }
            this.innerValue = value;
            this.tagData = data;
            if (needEmit) {
                this.$emit('input', value);
                this.$emit('change', value, items, deepClone(data));
                if (!isFromeValueChange) {
                    this.$emit('selected-nodes-change', value ? [].concat(items) : []);
                }
                this.dispatch('ElFormItem', 'el.form.change', value);
            }
        },
        filterData(keyword) {
            this.keyword = keyword;
        },
        showPopover() {
            this.popoverVisible = true;
        },
        closePopover() {
            this.popoverVisible = false;
        },
        selectAll() {
            if (this.checkbox) {
                this.$refs.treeWrapper.selectAll();
            }
        }
    }
};
</script>
<style lang="scss">
@import '../../styles/xx-ui/treeselect.scss';
</style>
